import React, { useEffect, useMemo, useState } from 'react';
import { SelectWithAdd } from 'components/Shared/SelectWithAdd';
import { SelectGroupOption } from 'components/Shared/SelectWithAdd/SelectWithAdd.types';
import { AddItemModal } from 'components/Shared/InputOutputModal/AddItemModal';
import {
  useCreateIoCategoryMutation,
  useGetIoCategoriesQuery,
} from 'redux/apiSlice/io.slice';
import {
  ENotificationType,
  useShowToastNotification,
} from 'components/Base/Notification';
import { formatStringToTitleCase } from 'helpers/utils';
import { EUserType } from 'data/types/user.types';
import { useAppSelector } from 'redux/store';
import {
  CategorySelectorProps,
  CategoryOption,
} from './CategorySelector.types';

const AEYC_VISIBLE_ROLES: EUserType[] = [
  EUserType.READY_ADMIN,
  EUserType.SUPER_ADMIN,
  EUserType.ELC_LEAD,
  EUserType.ELC_PM,
];

export const CategorySelector: React.FC<CategorySelectorProps> = ({
  value,
  onChange,
  placeholder = 'Select category',
  categoryType = 'MONEY',
  disabled = false,
  'data-cy': dataCy,
}) => {
  const user = useAppSelector(state => state.userSlice.user);
  const canSeeAeycCategories =
    user?.type?.some((role: EUserType) => AEYC_VISIBLE_ROLES.includes(role)) ??
    false;

  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const { showToastNotification } = useShowToastNotification();
  const [localCreatedOptions, setLocalCreatedOptions] = useState<
    CategoryOption[]
  >([]);

  const { data: categories = [], isFetching } = useGetIoCategoriesQuery(
    { type: categoryType },
    { skip: !categoryType, refetchOnMountOrArgChange: true },
  );

  const [createIoCategory, { isLoading: isCreating }] =
    useCreateIoCategoryMutation();

  const mergedOptions = useMemo(() => {
    const map = new Map<string, CategoryOption>();
    [...categories, ...localCreatedOptions].forEach(opt => {
      if (!map.has(opt.value)) map.set(opt.value, opt);
    });
    return Array.from(map.values());
  }, [categories, localCreatedOptions]);

  const selectOptions = useMemo(() => {
    const otherItems = mergedOptions.filter(opt => !opt.isAeyc);

    if (!canSeeAeycCategories) {
      return otherItems.map(({ label, value: v }) => ({ label, value: v }));
    }

    const aeycItems = mergedOptions.filter(opt => opt.isAeyc);

    if (aeycItems.length === 0) {
      return otherItems.map(({ label, value: v }) => ({ label, value: v }));
    }

    const groups: SelectGroupOption[] = [];
    groups.push({
      label: 'AEYC Categories',
      options: aeycItems.map(({ label, value: v }) => ({ label, value: v })),
    });
    groups.push({
      label: 'Other Categories',
      options: otherItems.map(({ label, value: v }) => ({ label, value: v })),
    });
    return groups;
  }, [mergedOptions, canSeeAeycCategories]);

  useEffect(() => {
    if (!categories?.length) return;
    setLocalCreatedOptions(prev =>
      prev.filter(loc => !categories.some(s => s.value === loc.value)),
    );
  }, [categories]);

  useEffect(() => {
    setLocalCreatedOptions([]);
  }, [categoryType]);

  const handleAddCategory = () => {
    setIsAddModalVisible(true);
  };

  const handleModalCancel = () => {
    setIsAddModalVisible(false);
  };

  const validateCategoryName = (categoryName: string) => {
    const categoryExists = mergedOptions.some(
      option => option.label.toLowerCase() === categoryName.toLowerCase(),
    );

    if (categoryExists) {
      return 'This category already exists.';
    }
    return undefined;
  };

  const handleCategoryAdd = async (categoryName: string) => {
    try {
      if (!categoryType) return;

      const newCategory: CategoryOption = await createIoCategory({
        type: categoryType,
        categoryName,
      }).unwrap();

      setLocalCreatedOptions(prev => [newCategory, ...prev]);

      onChange?.(newCategory.value);
      setIsAddModalVisible(false);
      showToastNotification({
        message: 'Category created',
        type: ENotificationType.SUCCESS,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to add category:', error);
      showToastNotification({
        message: 'Failed to create category',
        type: ENotificationType.ERROR,
      });
    }
  };

  const handleSelectionChange = (selectedValue: string) => {
    onChange?.(selectedValue);
  };

  return (
    <>
      <SelectWithAdd
        options={selectOptions}
        value={value}
        onChange={handleSelectionChange}
        placeholder={placeholder}
        onAdd={handleAddCategory}
        addButtonLabel="Add Category"
        emptyOptionsLabel="No categories"
        disabled={disabled || isFetching}
        data-cy={dataCy}
      />

      <AddItemModal
        visible={isAddModalVisible}
        onCancel={handleModalCancel}
        onAdd={handleCategoryAdd}
        title={`Add Category to ${formatStringToTitleCase(categoryType)}`}
        fieldLabel="Category Name"
        placeholder="Enter Name"
        loading={isCreating}
        validateInput={validateCategoryName}
        data-cy={`${dataCy}-add-modal`}
      />
    </>
  );
};

export default CategorySelector;
